import { Autocomplete, type Item, SelectInput, SelectInputItem } from '@ads/components-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetApiV1ExamDisciplineDropdownFiltered } from '@/shared/api/generated';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

import { useQuestionFormOptions } from '../../../../hooks/useQuestionFormOptions';

import type { UseQuestionEditFormReturn } from '../useQuestionEditForm';

const PARENT_QUESTION_OPTIONS: Item<string>[] = [];

interface DependencyTabProps {
  readonly state: UseQuestionEditFormReturn;
}

export function DependencyTab({ state }: DependencyTabProps) {
  const { t } = useTranslation('questionLibrary');
  const { t: tCommon } = useTranslation();
  const {
    depDiscipline,
    setDepDiscipline,
    depLanguage,
    setDepLanguage,
    depParentQuestion,
    setDepParentQuestion,
  } = state;

  const [disciplineSearch, setDisciplineSearch] = useState('');
  const [debouncedDisciplineFilter, setDebouncedDisciplineFilter] = useState('');

  useEffect(() => {
    if (disciplineSearch === '') {
      setDebouncedDisciplineFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedDisciplineFilter(disciplineSearch), 1000);
    return () => clearTimeout(timer);
  }, [disciplineSearch]);

  const isDisciplineReady = debouncedDisciplineFilter.length >= 3;
  const disciplinesQuery = useGetApiV1ExamDisciplineDropdownFiltered(
    isDisciplineReady ? { filter: debouncedDisciplineFilter } : undefined,
    { query: { enabled: isDisciplineReady } },
  );
  const { languageOptions } = useQuestionFormOptions();

  const disciplineOptions: Item<string>[] = (disciplinesQuery.data ?? []).map(o => ({
    label: o.value ?? '',
    value: String(o.key ?? ''),
  }));

  const selectedDiscipline = disciplineOptions.find(o => o.value === depDiscipline);
  const selectedParent = PARENT_QUESTION_OPTIONS.find(o => o.value === depParentQuestion);

  const disciplineMessage = getAutocompleteSearchMessage(
    disciplineSearch.length,
    disciplinesQuery.isSuccess,
    disciplineOptions.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: t('edit.dependency.notFound'),
    },
  );

  return (
    <div className="flex flex-col gap-xxs py-xs">
      <div className="grid grid-cols-2 gap-xs">
        <Autocomplete<string>
          size="md"
          label={t('edit.dependency.discipline')}
          list={disciplineOptions}
          custom={{ optionList: '' }}
          {...(disciplineMessage !== undefined ? { notFoundMessage: disciplineMessage } : {})}
          {...(selectedDiscipline !== undefined ? { selectedItem: selectedDiscipline } : {})}
          handleChange={item => {
            setDepDiscipline(item.value ?? '');
            setDisciplineSearch('');
          }}
          onChange={e => setDisciplineSearch(e.target.value)}
          handleClearQuery={() => {
            setDisciplineSearch('');
            setDepDiscipline('');
          }}
          placeholder={t('edit.dependency.disciplinePlaceholder')}
        />
        <SelectInput
          label={t('edit.dependency.language')}
          value={depLanguage}
          size="md"
          onValueChange={setDepLanguage}
          placeholder={t('edit.dependency.languagePlaceholder')}
        >
          {languageOptions.map(opt => (
            <SelectInputItem custom={{ root: 'text-nowrap' }} key={opt.value} value={opt.value}>
              {opt.label}
            </SelectInputItem>
          ))}
        </SelectInput>
      </div>

      <Autocomplete<string>
        label={t('edit.dependency.parentQuestion')}
        list={PARENT_QUESTION_OPTIONS}
        {...(selectedParent !== undefined ? { selectedItem: selectedParent } : {})}
        size="md"
        handleChange={item => setDepParentQuestion(item.value ?? '')}
        placeholder={t('edit.dependency.parentQuestionPlaceholder')}
        notFoundMessage={t('edit.dependency.notFound')}
      />
    </div>
  );
}
